import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ImagePlus, Loader2, Save, Trash2, FileText,
} from "lucide-react";
import {
  createPost, getAdminPost, updatePost, uploadImage, resolveAsset,
  formatApiErrorDetail, api,
} from "../lib/api";
import { usePostTypes } from "../lib/postTypes";
import RichTextEditor from "../components/RichTextEditor";
import CoverImageEditor from "../components/CoverImageEditor";
import MediaPicker from "../components/MediaPicker";


/**
 * Convert an ISO-8601 UTC timestamp (from the API) into the
 * "YYYY-MM-DDTHH:mm" shape required by an <input type="datetime-local"/>.
 * The input element interprets that string in the BROWSER's local zone,
 * so we offset the UTC value by the local zone before slicing.
 */
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const offsetMs = d.getTimezoneOffset() * 60_000;
  const local = new Date(d.getTime() - offsetMs);
  return local.toISOString().slice(0, 16);
}

/**
 * Inverse of `isoToLocalInput` — takes whatever the datetime-local input
 * emits (e.g. "2026-05-20T10:00", interpreted in local zone) and returns
 * a canonical ISO-8601 UTC string suitable for storing on the server.
 */
function localInputToIso(localStr) {
  if (!localStr) return "";
  const d = new Date(localStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}


export default function PostEditor({ mode = "new" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const coverRef = useRef(null);
  const galleryRef = useRef(null);
  const { types: postTypes } = usePostTypes();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [customIntro, setCustomIntro] = useState("");
  // Starts empty so the "default to first available type" effect below
  // can kick in once `usePostTypes()` has loaded. Edit mode overwrites
  // this with the post's saved value before the effect runs.
  const [postType, setPostType] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [coverLayout, setCoverLayout] = useState("hero"); // "hero" | "side"
  const [pickerOpen, setPickerOpen] = useState(null); // null | "cover" | "gallery"
  const [coverAspect, setCoverAspect] = useState(null);
  const [coverFocalX, setCoverFocalX] = useState(null);
  const [coverFocalY, setCoverFocalY] = useState(null);
  const [images, setImages] = useState([]);
  const [section, setSection] = useState("home");
  const [published, setPublished] = useState(true);
  const [archived, setArchived] = useState(false);
  const [authorName, setAuthorName] = useState("");
  // URL slug (user-facing canonical permalink). Empty string in edit
  // mode means "regenerate from the title on save".
  const [slug, setSlug] = useState("");
  // Short code is read-only — set by the backend when the post is
  // created. We surface it here so the admin can copy the share URL.
  const [shortCode, setShortCode] = useState("");
  // Used to build absolute share URLs.
  const siteOrigin = typeof window !== "undefined" ? window.location.origin : "";
  // `publishAt` holds an ISO-8601 UTC string ("2026-05-20T09:00:00.000Z")
  // or "" for "publish immediately". The <input type="datetime-local"/>
  // works in the browser's local zone — we convert in both directions.
  const [publishAt, setPublishAt] = useState("");
  // `displayAt` is an optional override for the date/time shown to readers
  // on the public post page. "" = use the post's created_at.
  const [displayAt, setDisplayAt] = useState("");
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [commentsAllowImages, setCommentsAllowImages] = useState(false);
  const bodyEditorRef = useRef(null);

  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [error, setError] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);

  useEffect(() => {
    if (mode !== "edit" || !id) return;
    let alive = true;
    getAdminPost(id)
      .then((p) => {
        if (!alive) return;
        setTitle(p.title);
        setBody(p.body);
        setCustomIntro(p.custom_intro || "");
        setPostType(p.post_type);
        setCoverImage(p.cover_image || null);
        setCoverAspect(p.cover_aspect ?? null);
        setCoverFocalX(p.cover_focal_x ?? null);
        setCoverFocalY(p.cover_focal_y ?? null);
        setCoverLayout(p.cover_layout || "hero");
        setImages(Array.isArray(p.images) ? p.images : []);
        setSection(p.section || "home");
        setPublished(p.published);
        setArchived(!!p.archived);
        setAuthorName(p.author_name || "");
        setSlug(p.slug || "");
        setShortCode(p.short_code || "");
        setPublishAt(p.publish_at || "");
        setDisplayAt(p.display_at || "");
        setCommentsEnabled(p.comments_enabled !== false);
        setCommentsAllowImages(p.comments_allow_images === true);
      })
      .catch((e) =>
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message)
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id, mode]);

  // "Use Template" — only fetched/relevant for brand-new posts, so it
  // never touches the existing edit flow.
  useEffect(() => {
    if (mode !== "new") return;
    let alive = true;
    api
      .get("/admin/post-templates")
      .then((r) => { if (alive) setTemplates(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setTemplates([]); });
    return () => { alive = false; };
  }, [mode]);

  const applyTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    if (body.trim() && !window.confirm(`Replace your current draft with the "${tpl.name}" template?`)) {
      setSelectedTemplateId("");
      return;
    }
    setBody(tpl.body || "");
    if (tpl.title) setTitle(tpl.title);
    if (tpl.post_type) setPostType(tpl.post_type);
    if (tpl.cover_image) setCoverImage(tpl.cover_image);
    if (tpl.custom_intro) setCustomIntro(tpl.custom_intro);
  };

  const openSaveAsTemplate = () => {
    setTemplateName(title.trim() || "Untitled template");
    setTemplateSaved(false);
    setShowSaveTemplate(true);
  };

  const confirmSaveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    setError("");
    try {
      await api.post("/admin/post-templates", {
        name: templateName.trim(),
        body,
        title,
        post_type: postType,
        cover_image: coverImage,
        custom_intro: customIntro,
      });
      setShowSaveTemplate(false);
      setTemplateSaved(true);
      setTimeout(() => setTemplateSaved(false), 2500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  // Default post type for NEW posts: the first entry from the admin's
  // own post-types list, sorted by position. This used to be hard-coded
  // to "long" (the built-in "Long Read"), but admins frequently delete
  // that type — leaving new posts stuck with a missing/orphaned type id
  // that the public site couldn't label. Now we pick whatever the admin
  // has put first in the list (e.g. "From LAST").
  useEffect(() => {
    if (mode !== "new") return;
    if (postType) return; // user (or initial state restore) already chose
    if (!Array.isArray(postTypes) || postTypes.length === 0) return;
    setPostType(postTypes[0].id);
  }, [mode, postType, postTypes]);

  const onPickCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("Image must be under 8 MB.");
      return;
    }
    setUploadingCover(true);
    setError("");
    try {
      const res = await uploadImage(file);
      setCoverImage(res.url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingCover(false);
    }
  };

  const onPickGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setError("");
    setUploadingGallery(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (file.size > 8 * 1024 * 1024) {
          setError(`${file.name} is over 8 MB — skipped.`);
          continue;
        }
        const res = await uploadImage(file);
        uploaded.push(res.url);
      }
      setImages((prev) => [...prev, ...uploaded]);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingGallery(false);
    }
  };

  const removeGalleryImage = (url) => {
    setImages((prev) => prev.filter((u) => u !== url));
  };

  const handleCopyIntroToBody = () => {
    if (bodyEditorRef.current) {
      bodyEditorRef.current.commands.setContent(customIntro, true);
      setBody(customIntro);
    }
  };

  const submit = async (e, continueEditing = false) => {
    if (e) e.preventDefault();
    setSaving(true);
    setError("");
    setSaveSuccess(false);
    try {
      const payload = {
        title,
        body,
        custom_intro: customIntro.trim() || null,
        post_type: postType,
        cover_image: coverImage,
        cover_aspect: coverAspect,
        cover_focal_x: coverFocalX,
        cover_focal_y: coverFocalY,
        cover_layout: coverLayout,
        images,
        section,
        published,
        archived,
        author_name: authorName.trim(),
        slug: slug.trim(),
        publish_at: publishAt || null,
        display_at: displayAt || null,
        comments_enabled: commentsEnabled,
        comments_allow_images: commentsAllowImages,
      };
      if (mode === "edit") {
        await updatePost(id, payload);
        if (!continueEditing) {
          navigate("/admin");
        } else {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        }
      } else {
        const res = await createPost(payload);
        if (!continueEditing) {
          navigate("/admin");
        } else {
          // Navigate to the edit screen of the newly created post so they can continue editing
          navigate(`/admin/posts/${res.id || res._id}/edit`, { replace: true });
        }
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="animate-spin text-[#6CABDD]" />
      </div>
    );
  }

  return (
    <div
      className="admin-chrome min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]"
      data-testid="page-post-editor"
    >
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 font-display text-sm font-semibold"
            data-testid="editor-back"
          >
            <ArrowLeft size={14} /> Back to admin
          </Link>
          <span className="label-kicker text-[var(--brand-body)]">
            {mode === "edit" ? "Edit post" : "New post"}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-12 sm:px-8">
        <form onSubmit={submit} className="space-y-6" data-testid="editor-form">
          {mode === "new" && templates.length > 0 ? (
            <div
              className="flex flex-wrap items-center gap-3 rounded-md border border-dashed border-[#6CABDD]/40 bg-[#6CABDD]/5 px-4 py-3"
              data-testid="editor-use-template"
            >
              <FileText size={16} className="shrink-0 text-[#6CABDD]" />
              <label className="text-sm font-semibold text-[var(--brand-ink)]">Use Template</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => applyTemplate(e.target.value)}
                data-testid="editor-template-select"
                className="min-w-[220px] flex-1 rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17] sm:flex-none"
              >
                <option value="">Start from scratch…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <span className="text-xs text-[var(--brand-body)]">Pre-fills title, post type, cover image, intro &amp; body below — you can still edit any of it.</span>
            </div>
          ) : null}

          <div>
            <label className="label-kicker text-[var(--brand-body)]">Title</label>
            <input
              required
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="editor-title"
              placeholder="Give this post a punchy headline"
              className="mt-2 block w-full rounded-md border border-black/15 bg-white px-4 py-3 font-display text-2xl font-bold tracking-tight outline-none focus:border-[#0A0F17]"
            />
          </div>

          <div>
            <label className="label-kicker text-[var(--brand-body)]">Author</label>
            <input
              maxLength={120}
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              data-testid="editor-author"
              placeholder="Defaults to your admin name"
              className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
            />
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              Shown as the byline on this post. Leave blank to use your admin name.
            </p>
          </div>

          {/* URL slug + short share link */}
          <div className="rounded-md border border-black/10 bg-white p-4">
            <label className="label-kicker text-[var(--brand-body)]">URL slug</label>
            <p className="mt-2 break-all font-mono text-[11px] text-[var(--brand-body)]">
              {siteOrigin}/post/<span className="text-[var(--brand-ink)]">{slug || "auto-from-title"}</span>
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                maxLength={120}
                value={slug}
                onChange={(e) =>
                  setSlug(
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]+/g, "-")
                      .replace(/^-+|-+$/g, "")
                  )
                }
                data-testid="editor-slug"
                placeholder="auto from title"
                className="block min-w-0 flex-1 rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17]"
              />
              {slug ? (
                <button
                  type="button"
                  data-testid="editor-slug-copy"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(`${siteOrigin}/post/${slug}`);
                    } catch (_) {
                      /* clipboard blocked — input remains selectable */
                    }
                  }}
                  className="shrink-0 rounded-md border border-black/15 bg-white px-3 py-2 font-display text-xs font-semibold hover:border-[#0A0F17]"
                >
                  Copy
                </button>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              The readable, SEO-friendly URL for this post. Leave blank to auto-generate from the title.
            </p>

            {shortCode ? (
              <div className="mt-4 border-t border-black/5 pt-3">
                <label className="label-kicker text-[var(--brand-body)]">
                  Short share link
                </label>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input
                    readOnly
                    value={`${siteOrigin}/s/${shortCode}`}
                    onFocus={(e) => e.target.select()}
                    data-testid="editor-short-url"
                    className="block min-w-0 flex-1 rounded-md border border-black/15 bg-[#F8FAFC] px-3 py-2 font-mono text-xs outline-none"
                  />
                  <button
                    type="button"
                    data-testid="editor-short-url-copy"
                    onClick={async () => {
                      const url = `${siteOrigin}/s/${shortCode}`;
                      try {
                        await navigator.clipboard.writeText(url);
                      } catch (_) {
                        /* clipboard blocked — input remains selectable */
                      }
                    }}
                    className="shrink-0 rounded-md border border-black/15 bg-white px-3 py-2 font-display text-xs font-semibold hover:border-[#0A0F17]"
                  >
                    Copy
                  </button>
                </div>
                <p className="mt-1 text-xs text-[var(--brand-body)]">
                  Tiny link that redirects to this post — perfect for X/Twitter. Stays the same even if you rename the post.
                </p>
              </div>
            ) : mode === "edit" ? (
              <p className="mt-3 text-xs text-[var(--brand-body)]">
                Short link will appear here after the first save.
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="label-kicker text-[var(--brand-body)]">Section</label>
              <div className="mt-2 flex gap-2" data-testid="editor-section">
                {[
                  { id: "home", label: "@LAST home" },
                  { id: "past", label: "Retro" },
                ].map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSection(s.id)}
                    data-testid={`editor-section-${s.id}`}
                    className={`flex-1 rounded-md border px-3 py-2 font-display text-sm font-semibold ${
                      section === s.id
                        ? "border-[#0A0F17] bg-[#0A0F17] text-white"
                        : "border-black/10 bg-white text-[var(--brand-body)]"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-kicker text-[var(--brand-body)]">Type</label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="editor-type">
                {(postTypes.length
                  ? postTypes
                  : []
                ).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setPostType(t.id)}
                    data-testid={`editor-type-${t.id}`}
                    className={`rounded-md border px-3 py-2 font-display text-sm font-semibold ${
                      postType === t.id
                        ? "border-[#0A0F17] bg-[#0A0F17] text-white"
                        : "border-black/10 bg-white text-[var(--brand-body)]"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-[var(--brand-body)]">
                Need another type? Add one at{" "}
                <Link to="/admin/post-types" className="underline decoration-[#6CABDD]">
                  /admin/post-types
                </Link>
                .
              </p>
            </div>
          </div>

          <div>
            <label className="label-kicker text-[var(--brand-body)]">Cover image (optional)</label>
            <div className="mt-2">
              <CoverImageEditor
                coverImage={coverImage}
                aspect={coverAspect}
                focalX={coverFocalX}
                focalY={coverFocalY}
                onChange={(patch) => {
                  if ("aspect" in patch) setCoverAspect(patch.aspect);
                  if ("focalX" in patch) setCoverFocalX(patch.focalX);
                  if ("focalY" in patch) setCoverFocalY(patch.focalY);
                }}
                onUpload={() => coverRef.current?.click()}
                onClear={() => {
                  setCoverImage(null);
                  setCoverAspect(null);
                  setCoverFocalX(null);
                  setCoverFocalY(null);
                }}
                uploading={uploadingCover}
              />
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setPickerOpen("cover")}
                  data-testid="editor-cover-from-library"
                  className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:text-[var(--brand-ink)] hover:underline"
                >
                  …or pick from media library →
                </button>
              </div>
              <input
                ref={coverRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={onPickCover}
                data-testid="editor-file-input"
              />
            </div>
          </div>

          {coverImage ? (
            <div>
              <label className="label-kicker text-[var(--brand-body)]">Cover Image Layout</label>
              <div className="mt-2 flex flex-col sm:flex-row gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="coverLayout"
                    value="hero"
                    checked={coverLayout === "hero"}
                    onChange={() => setCoverLayout("hero")}
                    data-testid="editor-cover-layout-hero"
                    className="accent-[#0A0F17]"
                  />
                  <span className="text-sm font-semibold text-[var(--brand-ink)]">Full Width Hero</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="coverLayout"
                    value="side"
                    checked={coverLayout === "side"}
                    onChange={() => setCoverLayout("side")}
                    data-testid="editor-cover-layout-side"
                    className="accent-[#0A0F17]"
                  />
                  <span className="text-sm font-semibold text-[var(--brand-ink)]">Side Thumbnail</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="coverLayout"
                    value="hidden"
                    checked={coverLayout === "hidden"}
                    onChange={() => setCoverLayout("hidden")}
                    data-testid="editor-cover-layout-hidden"
                    className="accent-[#0A0F17]"
                  />
                  <span className="text-sm font-semibold text-[var(--brand-ink)]">Hide inside post (Previews only)</span>
                </label>
              </div>
            </div>
          ) : null}

          <div>
            <label className="label-kicker text-[var(--brand-body)]">
              Gallery images (optional, shown inside the post)
            </label>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => galleryRef.current?.click()}
                disabled={uploadingGallery}
                data-testid="editor-gallery-upload"
                className="inline-flex items-center gap-2 rounded-md border border-black/10 bg-white px-4 py-2 font-display text-sm font-semibold hover:border-[#0A0F17]"
              >
                {uploadingGallery ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <ImagePlus size={14} />
                )}
                {uploadingGallery ? "Uploading…" : "Add images"}
              </button>
              <button
                type="button"
                onClick={() => setPickerOpen("gallery")}
                data-testid="editor-gallery-from-library"
                className="inline-flex items-center gap-1 label-kicker text-[var(--brand-body)] underline-offset-2 hover:text-[var(--brand-ink)] hover:underline"
              >
                …or pick from library →
              </button>
              <span className="label-kicker text-[var(--brand-body)]">
                {images.length} image{images.length === 1 ? "" : "s"}
              </span>
              <input
                ref={galleryRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                multiple
                onChange={onPickGallery}
                data-testid="editor-gallery-input"
              />
            </div>
            {images.length > 0 ? (
              <div
                className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4"
                data-testid="editor-gallery-preview"
              >
                {images.map((url) => (
                  <div
                    key={url}
                    className="group relative overflow-hidden rounded-md border border-black/10"
                  >
                    <img
                      src={resolveAsset(url)}
                      alt=""
                      className="h-28 w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(url)}
                      className="absolute right-1 top-1 inline-flex h-7 w-7 items-center justify-center rounded-sm bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      aria-label="Remove image"
                      data-testid="editor-gallery-remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <label className="label-kicker text-[var(--brand-body)]">
              Custom intro (optional)
            </label>
            <div className="mt-2" data-testid="editor-custom-intro">
              <RichTextEditor
                value={customIntro}
                onChange={setCustomIntro}
                minHeight={210}
                placeholder="Write a custom teaser for the preview cards on the home page and archive. Leave blank to auto-use the start of the post body."
              />
            </div>
            <p className="mt-2 text-xs text-[var(--brand-body)]">
              Shown on the home + archive preview cards instead of the
              auto-generated start of the post. Inline formatting (bold,
              italic, links) is preserved on cards; block elements collapse
              to plain text. Leave blank to fall back to the auto excerpt.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="label-kicker text-[var(--brand-body)]">Body</label>
              <button
                type="button"
                onClick={handleCopyIntroToBody}
                disabled={!customIntro.trim()}
                data-testid="editor-copy-intro-btn"
                className="rounded-md border border-black/10 bg-white px-3 py-1 font-display text-xs font-semibold text-[var(--brand-body)] hover:border-[#0A0F17] hover:text-[var(--brand-ink)] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Copy intro to body
              </button>
            </div>
            <div className="mt-2" data-testid="editor-body">
              <RichTextEditor
                value={body}
                onChange={setBody}
                editorRef={bodyEditorRef}
                placeholder="Write your post."
              />
            </div>
            <p className="mt-2 text-xs text-[var(--brand-body)]">
              Headings, bold/italic, lists, blockquote, images and links.
              Click an inserted image to resize it by dragging a corner
              handle, or position it left/right so text wraps beside it
              (stacks above the text on mobile), or centre it with no wrap.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
                data-testid="editor-published"
                className="h-4 w-4 accent-[#6CABDD]"
              />
              <span className="font-display font-semibold">
                Publish immediately
              </span>
              <span className="text-[var(--brand-body)]">
                (uncheck to save as a draft)
              </span>
            </label>

            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={archived}
                onChange={(e) => setArchived(e.target.checked)}
                data-testid="editor-archived"
                className="h-4 w-4 accent-[#475569]"
              />
              <span className="font-display font-semibold">
                Archive this post
              </span>
              <span className="text-[var(--brand-body)]">
                (hidden from the home feed; still readable + searchable in the
                archive)
              </span>
            </label>

            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={commentsEnabled}
                onChange={(e) => setCommentsEnabled(e.target.checked)}
                data-testid="editor-comments-enabled"
                className="h-4 w-4 accent-[#6CABDD]"
              />
              <span className="font-display font-semibold">
                Allow comments
              </span>
              <span className="text-[var(--brand-body)]">
                (uncheck to disable the comments box on this post)
              </span>
            </label>

            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={commentsAllowImages}
                onChange={(e) => setCommentsAllowImages(e.target.checked)}
                data-testid="editor-comments-allow-images"
                className="h-4 w-4 accent-[#6CABDD]"
              />
              <span className="font-display font-semibold">
                Allow image uploads in comments
              </span>
              <span className="text-[var(--brand-body)]">
                (off by default — when on, this post appears in the Lineup
                Builder's "Post to Match Comments" dropdown so fans can
                attach their lineup JPEG to a comment here)
              </span>
            </label>
          </div>

          <div className="rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
            <label className="block">
              <span className="font-display text-sm font-bold text-[var(--brand-ink)]">
                Schedule (optional)
              </span>
              <p className="mt-1 text-xs text-[var(--brand-body)]">
                Leave blank to publish straight away. Set a future date/time
                to keep the post hidden from fans until that moment passes —
                handy for matchday anniversaries or "On This Day" memories.
                Times are stored as UTC; the picker shows your local zone.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  type="datetime-local"
                  value={isoToLocalInput(publishAt)}
                  onChange={(e) => setPublishAt(localInputToIso(e.target.value))}
                  data-testid="editor-publish-at"
                  className="rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm text-[var(--brand-ink)] outline-none focus:border-[#0A0F17]"
                />
                {publishAt ? (
                  <>
                    <span className="label-kicker text-[var(--brand-body)]">
                      Scheduled for{" "}
                      {new Date(publishAt).toLocaleString("en-GB", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPublishAt("")}
                      data-testid="editor-clear-publish-at"
                      className="label-kicker text-[#E63946] underline underline-offset-4"
                    >
                      Clear
                    </button>
                  </>
                ) : (
                  <span className="label-kicker text-[var(--brand-body)]">
                    Publishes immediately
                  </span>
                )}
              </div>
            </label>
          </div>

          <div className="rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
            <label className="block">
              <span className="font-display text-sm font-bold text-[var(--brand-ink)]">
                Display date (optional)
              </span>
              <p className="mt-1 text-xs text-[var(--brand-body)]">
                Override the date/time shown to readers on the public post
                page. Leave blank to use the post's actual creation date —
                useful for Retro posts where you want "13/05/1997, 17:00:00"
                to appear instead of today's date.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  type="datetime-local"
                  step="1"
                  value={isoToLocalInput(displayAt)}
                  onChange={(e) => setDisplayAt(localInputToIso(e.target.value))}
                  data-testid="editor-display-at"
                  className="rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm text-[var(--brand-ink)] outline-none focus:border-[#0A0F17]"
                />
                {displayAt ? (
                  <>
                    <span className="label-kicker text-[var(--brand-body)]">
                      Readers see{" "}
                      {new Date(displayAt).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: false,
                      })}
                    </span>
                    <button
                      type="button"
                      onClick={() => setDisplayAt("")}
                      data-testid="editor-clear-display-at"
                      className="label-kicker text-[#E63946] underline underline-offset-4"
                    >
                      Clear
                    </button>
                  </>
                ) : (
                  <span className="label-kicker text-[var(--brand-body)]">
                    Uses the post's actual creation date
                  </span>
                )}
              </div>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] px-4 py-3">
            <FileText size={14} className="shrink-0 text-[#6CABDD]" />
            <p className="text-sm text-[var(--brand-body)]">
              Like this post? Save the title, post type, cover image, intro &amp; body as a reusable template.
            </p>
            <button
              type="button"
              onClick={openSaveAsTemplate}
              data-testid="editor-save-as-template"
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-black/15 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17]"
            >
              <Save size={12} /> Save as Template
            </button>
            {templateSaved ? (
              <span className="text-xs font-bold text-emerald-600" data-testid="editor-save-as-template-success">
                ✓ Template saved
              </span>
            ) : null}
          </div>

          {showSaveTemplate ? (
            <div
              className="flex flex-wrap items-center gap-3 rounded-md border border-[#6CABDD]/40 bg-[#6CABDD]/5 px-4 py-3"
              data-testid="editor-save-as-template-panel"
            >
              <label className="flex-1 min-w-[220px]">
                <span className="label-kicker text-[var(--brand-body)]">Template name</span>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  maxLength={80}
                  autoFocus
                  data-testid="editor-save-as-template-name"
                  className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                />
              </label>
              <button
                type="button"
                onClick={confirmSaveAsTemplate}
                disabled={savingTemplate || !templateName.trim()}
                data-testid="editor-save-as-template-confirm"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-xs font-bold uppercase tracking-wide text-white hover:bg-black disabled:opacity-40"
              >
                {savingTemplate ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save
              </button>
              <button
                type="button"
                onClick={() => setShowSaveTemplate(false)}
                data-testid="editor-save-as-template-cancel"
                className="text-xs font-semibold text-[var(--brand-body)] underline underline-offset-4"
              >
                Cancel
              </button>
            </div>
          ) : null}

          {error ? (
            <p
              className="rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm text-[var(--brand-ink)]"
              data-testid="editor-error"
            >
              {error}
            </p>
          ) : null}

          <div className="flex items-center gap-3 border-t border-black/10 pt-6">
            <button
              type="submit"
              disabled={saving}
              data-testid="editor-save"
              className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-5 py-3 font-display text-sm font-bold uppercase tracking-wide text-white hover:bg-[#000] disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Publish post"}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={(e) => submit(e, true)}
              data-testid="editor-save-continue"
              className="inline-flex items-center gap-2 rounded-md border border-[#0A0F17] px-5 py-3 font-display text-sm font-bold uppercase tracking-wide text-[var(--brand-ink)] hover:bg-[#0A0F17]/5 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save & stay on page"}
            </button>
            <Link
              to="/admin"
              className="label-kicker text-[var(--brand-body)] underline underline-offset-4"
            >
              Cancel
            </Link>
            {saveSuccess ? (
              <span className="text-sm font-bold text-emerald-600 flex items-center gap-1" data-testid="editor-save-success">
                ✓ Saved!
              </span>
            ) : null}
          </div>
        </form>
      </main>
      <MediaPicker
        open={pickerOpen !== null}
        onClose={() => setPickerOpen(null)}
        onSelect={(url) => {
          if (pickerOpen === "cover") {
            setCoverImage(url);
          } else if (pickerOpen === "gallery") {
            setImages((prev) => [...prev, url]);
          }
          setPickerOpen(null);
        }}
        accept="image"
      />
    </div>
  );
}
